import { sanitizeNumeric } from '../../../utils/numericInput';

/**
 * Smart Autofill scan result → Add Asset form mapping.
 *
 * Shared by the create-time autofill (AddAsset `handleScanData`, which fills
 * the blank form directly) and the edit-page re-scan (ItemAutofillPanel,
 * which turns the same mapping into reviewable suggestions). One mapping so
 * the two flows can't drift on which scanner key feeds which form field.
 */

// Backend (schemas/item.py BaseProperties._reject_promoted_keys) rejects any
// write where these keys appear inside the `properties` JSON — they live as
// top-level Item columns instead. Strip them defensively before submit so
// stale form state or scanner output can't trigger a 400.
export const PROMOTED_PROPERTY_KEYS = ['color'];

export const splitPromotedKeys = (properties) => {
    const promoted = {};
    const remaining = { ...(properties || {}) };
    PROMOTED_PROPERTY_KEYS.forEach((key) => {
        if (remaining[key] !== undefined && remaining[key] !== null && remaining[key] !== '') {
            promoted[key] = remaining[key];
        }
        delete remaining[key];
    });
    return { promoted, remaining };
};

// Scanner keys that feed a top-level form field or are scan metadata.
// `color` is here because it's a top-level Item column, not a free-form
// property — see PROMOTED_PROPERTY_KEYS. Anything not in this list spills
// into `properties`.
const CORE_SCAN_KEYS = new Set([
    'name', 'item_name', 'manufacturer', 'brand', 'category', 'item_category', 'item_subcategory',
    'model', 'model_number', 'serial', 'serial_number', 'date', 'purchase_date', 'value',
    'purchase_price', 'estimated_price', 'estimated_value', 'price', 'color', 'notes',
    'description', 'quantity', 'item_type', 'success', 'confidence', 'properties',
]);

const hasValue = (v) => v !== null && v !== undefined && v !== '';

/**
 * Split a scanned item into form fields and extra properties.
 *
 * `fields` holds one entry per form field ('' when the scan had nothing for
 * it); `properties` holds everything else the scan returned, with promoted
 * keys (color) hoisted into `fields`.
 */
export const mapScanToForm = (data = {}) => {
    const collected = {};
    if (data.properties && typeof data.properties === 'object') {
        Object.entries(data.properties).forEach(([k, v]) => {
            if (hasValue(v)) collected[k] = v;
        });
    }
    Object.keys(data).forEach((key) => {
        if (!CORE_SCAN_KEYS.has(key) && hasValue(data[key])) collected[key] = data[key];
    });

    // Belt-and-suspenders: even with the server-side hoist in
    // utils/universal_scanner.py, strip promoted keys here so any
    // direct-from-AI consumer or future scanner can't smuggle them in.
    const { promoted, remaining } = splitPromotedKeys(collected);

    return {
        fields: {
            name: data.item_name || data.name || data.brand || '',
            manufacturer: data.manufacturer || data.brand || '',
            category: data.item_category || data.category || '',
            item_subcategory: data.item_subcategory || '',
            model: data.model_number || data.model || '',
            serial: data.serial_number || data.serial || '',
            date: data.purchase_date || data.date || '',
            value: data.purchase_price || '',
            color: data.color || promoted.color || '',
            notes: data.description || data.notes || '',
        },
        properties: remaining,
    };
};

// ---------------------------------------------------------------------------
// Edit-mode review: compare a scan against the item's current form values.
// ---------------------------------------------------------------------------

const isBlank = (v) => {
    if (v === null || v === undefined) return true;
    if (typeof v === 'string') return v.trim() === '';
    if (Array.isArray(v)) return v.length === 0;
    if (typeof v === 'object') return Object.keys(v).length === 0;
    return false;
};

const comparable = (v) => (
    typeof v === 'object' ? JSON.stringify(v) : String(v).trim().toLowerCase()
);

// Case/whitespace-insensitive, so "SAMSUNG" vs "Samsung" isn't offered as a change.
const sameValue = (a, b) => {
    if (isBlank(a) || isBlank(b)) return isBlank(a) && isBlank(b);
    return comparable(a) === comparable(b);
};

const currencyFormatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

const displayValue = (v) => {
    if (isBlank(v)) return null;
    if (Array.isArray(v)) return v.map(displayValue).filter(Boolean).join(', ');
    if (typeof v === 'object') {
        return Object.entries(v).map(([k, val]) => `${k}: ${displayValue(val) ?? '—'}`).join(', ');
    }
    return String(v).trim();
};

const displayPrice = (v) => {
    const n = sanitizeNumeric(v);
    return n == null ? displayValue(v) : currencyFormatter.format(n);
};

const humanizeKey = (key) => {
    const spaced = String(key).replace(/_/g, ' ').trim();
    return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

// Plain text fields that follow Category, in AssetForm order.
const TEXT_FIELDS = [
    { key: 'manufacturer', label: 'Manufacturer' },
    { key: 'model', label: 'Model Number' },
    { key: 'serial', label: 'Serial Number' },
    { key: 'color', label: 'Color/Finish' },
];

const joinCategory = (category, subcategory) => {
    if (isBlank(category)) return null;
    return isBlank(subcategory) ? category : `${category} › ${subcategory}`;
};

/**
 * Turn a scan into the list of changes it would make to the edit form.
 *
 * Each suggestion is
 *   { id, kind: 'field'|'property', label, current, suggested,
 *     replacesValue, fields?, properties? }
 * where `current`/`suggested` are display strings (current is null when the
 * field is empty), `replacesValue` is true when applying it would overwrite
 * something the item already has, and `fields`/`properties` are what
 * applyAutofillSuggestions merges into the form. Values the item already
 * has (compared case-insensitively) produce no suggestion.
 */
export const buildAutofillSuggestions = (formData, scanItem) => {
    if (!scanItem) return [];
    const { fields, properties } = mapScanToForm(scanItem);
    const currentProps = formData.properties || {};
    const suggestions = [];

    // Text inputs hold strings, and the item schema expects them — a scanner
    // returning a numeric model number must not reach the payload as a number.
    const addField = (key, label, raw) => {
        const suggestedValue = isBlank(raw) ? '' : String(raw).trim();
        if (suggestedValue === '' || sameValue(formData[key], suggestedValue)) return;
        suggestions.push({
            id: key,
            kind: 'field',
            label,
            current: displayValue(formData[key]),
            suggested: suggestedValue,
            replacesValue: !isBlank(formData[key]),
            fields: { [key]: suggestedValue },
        });
    };

    addField('name', 'Asset Name', fields.name);

    // Category and subcategory move together: a subcategory only means
    // something within its category, so a category change carries the
    // scanned subcategory (or clears the old one) rather than leaving a
    // stale pairing behind.
    if (!isBlank(fields.category) || !isBlank(fields.item_subcategory)) {
        const categoryChanges = !isBlank(fields.category) && !sameValue(fields.category, formData.category);
        const nextCategory = categoryChanges ? fields.category : formData.category;
        const nextSubcategory = categoryChanges
            ? (fields.item_subcategory || '')
            : (fields.item_subcategory || formData.item_subcategory || '');
        if (categoryChanges || !sameValue(nextSubcategory, formData.item_subcategory)) {
            suggestions.push({
                id: 'category',
                kind: 'field',
                label: 'Category',
                current: joinCategory(formData.category, formData.item_subcategory),
                suggested: joinCategory(nextCategory, nextSubcategory),
                replacesValue: categoryChanges
                    ? !isBlank(formData.category)
                    : !isBlank(formData.item_subcategory),
                fields: { category: nextCategory, item_subcategory: nextSubcategory },
            });
        }
    }

    TEXT_FIELDS.forEach(({ key, label }) => addField(key, label, fields[key]));

    // Only a well-formed YYYY-MM-DD can populate the date input.
    const scannedDate = String(fields.date || '');
    if (ISO_DATE.test(scannedDate)) addField('date', 'Purchase Date', scannedDate.substring(0, 10));

    const scannedPrice = sanitizeNumeric(fields.value);
    if (scannedPrice != null && sanitizeNumeric(formData.value) !== scannedPrice) {
        suggestions.push({
            id: 'value',
            kind: 'field',
            label: 'Purchase Price',
            current: displayPrice(formData.value),
            suggested: displayPrice(scannedPrice),
            replacesValue: !isBlank(formData.value),
            fields: { value: String(scannedPrice) },
        });
    }

    addField('notes', 'Notes & Details', fields.notes);

    Object.entries(properties).forEach(([key, value]) => {
        if (isBlank(value) || sameValue(currentProps[key], value)) return;
        suggestions.push({
            id: `property:${key}`,
            kind: 'property',
            label: humanizeKey(key),
            current: displayValue(currentProps[key]),
            suggested: displayValue(value),
            replacesValue: !isBlank(currentProps[key]),
            properties: { [key]: value },
        });
    });

    return suggestions;
};

/** Merge the given suggestions into a copy of the form state. */
export const applyAutofillSuggestions = (formData, suggestions) => {
    const next = { ...formData, properties: { ...(formData.properties || {}) } };
    suggestions.forEach((s) => {
        Object.assign(next, s.fields || {});
        Object.assign(next.properties, s.properties || {});
    });
    return next;
};
